import axios, { AxiosResponse } from "axios";
import { environment } from "../../environment";
import { useLogin } from "../login/login";
import {
    ArtistInfluencerErrorCode,
    IArtistInfluencer,
    IArtistInfluencerError,
    IBrandSearchResult,
    IBulkAddResult,
    IInfluencerMe,
    IInfluencerMember,
    IInfluencerMemberInput
} from "./types";

/**
 * Services for `/api/v2/artist-influencers/*` (super-admin) and
 * `/api/v2/influencer-roster/*` (influencer-facing).
 *
 * Follows the internal-orders convention rather than `errorCatcher`: every
 * method resolves either the payload or a typed `IArtistInfluencerError` and
 * never throws, because the UI has to branch on `code` — EMAIL_ON_ANOTHER_ROSTER
 * pins a per-row message, NOT_AN_INFLUENCER hides the whole tab. A 401 still
 * logs out for parity with the shared catchers.
 */

const adminUrl = `${environment.v2Url}/artist-influencers`;
const rosterUrl = `${environment.v2Url}/influencer-roster`;

export type ArtistInfluencersKeyFunctions = {
    listInfluencers: () => Promise<IArtistInfluencer[] | IArtistInfluencerError>;
    setInfluencer: (brandId: number, discountPercent: number) => Promise<IArtistInfluencer | IArtistInfluencerError>;
    removeInfluencer: (brandId: number) => Promise<{ success: true } | IArtistInfluencerError>;
    searchBrands: (query: string) => Promise<IBrandSearchResult[] | IArtistInfluencerError>;
};

export type InfluencerRosterKeyFunctions = {
    getMe: () => Promise<IInfluencerMe | IArtistInfluencerError>;
    listMembers: () => Promise<IInfluencerMember[] | IArtistInfluencerError>;
    addMember: (row: IInfluencerMemberInput) => Promise<IBulkAddResult | IArtistInfluencerError>;
    bulkAddMembers: (rows: IInfluencerMemberInput[]) => Promise<IBulkAddResult | IArtistInfluencerError>;
    removeMember: (memberId: number) => Promise<{ success: true } | IArtistInfluencerError>;
};

// #region toInfluencerError
const toInfluencerError = (err: any): IArtistInfluencerError => {
    const body = err?.response?.data;
    const status = err?.response?.status ?? 0;

    if (body && typeof body === 'object' && typeof body.code === 'string') {
        return { ok: false, code: body.code as ArtistInfluencerErrorCode, message: body.message || 'Something went wrong.', status };
    }

    if (status === 0) {
        return {
            ok: false,
            code: ArtistInfluencerErrorCode.INTERNAL_ERROR,
            message: 'Could not reach Wallmates. Check your connection and try again.',
            status
        };
    }

    return {
        ok: false,
        code: ArtistInfluencerErrorCode.INTERNAL_ERROR,
        message: typeof body?.message === 'string' ? body.message : 'Something went wrong. Please try again.',
        status
    };
};

// #region useArtistInfluencers
export const useArtistInfluencers = (): [ArtistInfluencersKeyFunctions] => {
    const [, authToken, loginFunctions] = useLogin();
    const headers = { 'X-Wallmates-Auth': authToken };

    const handle = (err: any): IArtistInfluencerError => {
        if (err?.response?.status === 401) loginFunctions.logout();
        return toInfluencerError(err);
    };

    // #region listInfluencers
    const listInfluencers = async (): Promise<IArtistInfluencer[] | IArtistInfluencerError> => {
        try {
            const result: AxiosResponse<IArtistInfluencer[]> = await axios.get(adminUrl, { headers });
            return result.data;
        } catch (err: any) {
            return handle(err);
        }
    };

    // #region setInfluencer
    // Flags a brand or edits its percentage. The new percentage applies to every
    // currently linked artist on FUTURE orders — placed orders are never re-priced.
    const setInfluencer = async (
        brandId: number,
        discountPercent: number
    ): Promise<IArtistInfluencer | IArtistInfluencerError> => {
        try {
            const result: AxiosResponse<IArtistInfluencer> = await axios.put(
                `${adminUrl}/${brandId}`,
                { discount_percent: discountPercent },
                { headers }
            );
            return result.data;
        } catch (err: any) {
            return handle(err);
        }
    };

    // #region removeInfluencer
    const removeInfluencer = async (brandId: number): Promise<{ success: true } | IArtistInfluencerError> => {
        try {
            const result: AxiosResponse<{ success: true }> = await axios.delete(`${adminUrl}/${brandId}`, { headers });
            return result.data;
        } catch (err: any) {
            return handle(err);
        }
    };

    // #region searchBrands
    const searchBrands = async (query: string): Promise<IBrandSearchResult[] | IArtistInfluencerError> => {
        try {
            const result: AxiosResponse<IBrandSearchResult[]> = await axios.get(
                `${adminUrl}/brands/search?q=${encodeURIComponent(query)}`,
                { headers }
            );
            return result.data;
        } catch (err: any) {
            return handle(err);
        }
    };

    return [{ listInfluencers, setInfluencer, removeInfluencer, searchBrands }];
};

// #region useInfluencerRoster
export const useInfluencerRoster = (): [InfluencerRosterKeyFunctions] => {
    const [, authToken, loginFunctions] = useLogin();
    const headers = { 'X-Wallmates-Auth': authToken };

    const handle = (err: any): IArtistInfluencerError => {
        if (err?.response?.status === 401) loginFunctions.logout();
        return toInfluencerError(err);
    };

    // #region getMe
    const getMe = async (): Promise<IInfluencerMe | IArtistInfluencerError> => {
        try {
            const result: AxiosResponse<IInfluencerMe> = await axios.get(`${rosterUrl}/me`, { headers });
            return result.data;
        } catch (err: any) {
            return handle(err);
        }
    };

    // #region listMembers
    const listMembers = async (): Promise<IInfluencerMember[] | IArtistInfluencerError> => {
        try {
            const result: AxiosResponse<IInfluencerMember[]> = await axios.get(`${rosterUrl}/members`, { headers });
            return result.data;
        } catch (err: any) {
            return handle(err);
        }
    };

    // #region addMember
    // Returns the same result shape as the bulk path so one summary component
    // renders both.
    const addMember = async (row: IInfluencerMemberInput): Promise<IBulkAddResult | IArtistInfluencerError> => {
        try {
            const result: AxiosResponse<IBulkAddResult> = await axios.post(`${rosterUrl}/members`, row, { headers });
            return result.data;
        } catch (err: any) {
            return handle(err);
        }
    };

    // #region bulkAddMembers
    // Partial success is expected: a 200 can still carry per-row errors.
    const bulkAddMembers = async (
        rows: IInfluencerMemberInput[]
    ): Promise<IBulkAddResult | IArtistInfluencerError> => {
        try {
            const result: AxiosResponse<IBulkAddResult> = await axios.post(
                `${rosterUrl}/members/bulk`,
                { rows },
                { headers }
            );
            return result.data;
        } catch (err: any) {
            return handle(err);
        }
    };

    // #region removeMember
    const removeMember = async (memberId: number): Promise<{ success: true } | IArtistInfluencerError> => {
        try {
            const result: AxiosResponse<{ success: true }> = await axios.delete(
                `${rosterUrl}/members/${memberId}`,
                { headers }
            );
            return result.data;
        } catch (err: any) {
            return handle(err);
        }
    };

    return [{ getMe, listMembers, addMember, bulkAddMembers, removeMember }];
};
